import { get } from "../utils/helpers";
import { toLowerCase } from "../utils/toLowerCase";
import { checkIsEmptyArray } from "../utils/isEmptyArray";
import { getCookie } from "../utils/cookie";
import { WalletInfo } from "@nykaa/payment-sdk/src/Payment/@types";
import { ICartInfo } from "@nykaa/payment-sdk/src/Payment/@types";
import { paymentInfoInterface, IDownBanksDetails } from "@nykaa/payment-sdk/src/Payment/@types";
import { PAYMENT_MODES } from "constants/paymentModes";
import { ACCORDIAN } from "constants/index";
import { CLIENT_URLS } from "constants/urls";
import { doRedirect } from "helpers/utils/redirection";
import { ICartConvenienceChargesBreakupDto } from "store/payment/transformers/cart/shippingDataDTOTransformer";
import { PAYMENT_FLOWS } from "constants/payment";
import { checkIsBeautyPlatform } from "helpers/utils/redirectToCheckoutPage";
import { isServer } from "helpers/utils/isServer";

/**
 * @param {object} cartInfo: contains information of the cart
 * @description
 * Function to get the payment tags which came from the cart API.
 * It could be an array of string or null.
 * @returns {string} return the payment tags string with comma seperated.
 */
export const getPaymentTagsString = (paymentTags?: string[]) => {
  try {
    return Array.isArray(paymentTags) ? paymentTags.join() : "";
  } catch (err) {
    // logger.error(err, 'payment tags should be an array or null');
  }
  return "";
};

/**
 * decides whether nykaa wallet is enabled or not
 * @param {boolean} scWalletFlag - flag that determines whether semi closed wallet is enabled for user
 * @returns {boolean} determines visibility of wallet
 */
export const checkIsNykaaWalletEnabled = ({
  scWalletFlag,
}: {
  scWalletFlag: number
}) => {
  /**
   *  wallet is enabled when included in available methods and scWalletFlag is true
   *  https://nykmage.atlassian.net/browse/FC-4099
   */

  if (
      scWalletFlag
  ) {
    return true;
  }

  return false; // hide wallet when none of the above conditions satisfy
};

/**
 * @description -- function to get the saved methods which are also in available list
 * on the basis of some savedCardDTOList, lastPaymentInfo, isPaytmConnected, customerVpaList
 * @returns -- list of saved methods(i.e. 'cc', 'upi' etc ) that are also in available list
 */
export const getSavedAvailableMethods = ({
  savedMethods,
  paymentInfo,
  availableMethods,
  pciEnabled,
}: {
  savedMethods: string[];
  paymentInfo: paymentInfoInterface;
  availableMethods: string[];
  pciEnabled: boolean
  }) => {
  let savedMethodArray = [];
  const lastPaymentInfo = get(paymentInfo, "lastPaymentInfo");
  savedMethodArray = savedMethods.map((method: string) => {
    // return; returns "undefined" value from the function
    switch (method) {
      case ACCORDIAN.LAST_METHOD:
        /* eslint-disable no-case-declarations */
        const isLastPaymentInfoVisible = checkIfLastPaymentInfoVisible(
          paymentInfo,
          availableMethods,
          pciEnabled
        );
        if (!isLastPaymentInfoVisible) return;
        break;
      case PAYMENT_MODES.CREDIT_CARD || PAYMENT_MODES.DEBIT_CARD:
        // if savedCardDTOList has items and it is available in availableMethods, then add 'cc' to savedMethodArray
        const savedCardDTOList = get(paymentInfo, "savedCardDTOList");
        if (
          !(
            Array.isArray(savedCardDTOList) &&
            savedCardDTOList.length &&
            availableMethods.includes(
              PAYMENT_MODES.CREDIT_CARD || PAYMENT_MODES.DEBIT_CARD
            )
          )
        )
          return;
        break;
      case PAYMENT_MODES.NET_BANKING:
        // if payment mode in lastPaymentInfo in netbanking and it is available in availableMethods, then add 'nb' to savedMethodArray
        if (
          !(
            lastPaymentInfo &&
            toLowerCase(lastPaymentInfo.paymentMode) ===
              PAYMENT_MODES.NET_BANKING
          ) &&
          availableMethods.includes(PAYMENT_MODES.NET_BANKING)
        )
          return;
        break;
      // if paytm is linked(isPaytmConnected is true) and it is available in availableMethods, then add 'paytm' to savedMethodArray
      case PAYMENT_MODES.PAYTM:
        const isPaytmConnected = get(paymentInfo, "isPaytmConnected");
        if (
          !(isPaytmConnected && availableMethods.includes(PAYMENT_MODES.PAYTM))
        )
          return;
        break;
      case PAYMENT_MODES.UPI:
        // when customerVpaList has items and availableMethods have upi, then add 'upi' to savedMethodArray
        const customerVpaList = get(paymentInfo, "customerVpaList");
        if (
          !(
            Array.isArray(customerVpaList) &&
            customerVpaList.length &&
            availableMethods.includes(PAYMENT_MODES.UPI)
          )
        )
          return;
        if (
          customerVpaList?.length === 1 &&
          lastPaymentInfo?.paymentMode?.toLowerCase() ===
            ACCORDIAN.GPAY_INTENT &&
          customerVpaList[0]?.vpa === lastPaymentInfo?.paymentInfo
        )
          return;
        break;
      case PAYMENT_MODES.SIMPL:
        const simplPhoneNumber = get(paymentInfo, "simplDTO.phone");
        if (
          !(simplPhoneNumber && availableMethods.includes(PAYMENT_MODES.SIMPL))
        )
          return;
        break;
      default:
        break;
    }
    return method;
  });
  return savedMethodArray;
};

/**
 * checkLastPaymentInfoNBDown -- function to check whether lastPaymentInfo netbanking is down or not
 */
export const checkLastPaymentInfoNBDown = (paymentInfo: paymentInfoInterface) => {
  const lastPaymentInfo = get(paymentInfo, "lastPaymentInfo");
  const downBanksList = get(paymentInfo, "downBanksList");
  let isLastPaymentNBDown = false;
  const lastPaymentInfoBankDown = downBanksList?.length && downBanksList.find((bank: IDownBanksDetails) => lastPaymentInfo?.paymentInfo === bank.payuCode || lastPaymentInfo?.paymentInfo === bank.razorpayCode || lastPaymentInfo?.paymentInfo === bank.cashfreeCode );
  // if lastPaymentInfo bank is in downBanksList list
  if (lastPaymentInfoBankDown) {
    isLastPaymentNBDown = true;
  }
  return isLastPaymentNBDown;
};

export const checkIfLastPaymentInfoVisible = (
  paymentInfo: paymentInfoInterface,
  availableMethods: string[],
  pciEnabled: boolean
) => {
  const lastPaymentInfo = get(paymentInfo, "lastPaymentInfo");
  /**
   * paymentMode in lastPaymentInfo are in uppercase, we have checked for NB
   * but on availablePaymentMethods array all paymentMode are in lowercase
   * so specifically we have lowercase the paymentMode from lastPaymentInfo
   */
  const lastPaymentMode = toLowerCase(lastPaymentInfo?.paymentMode);

  let lastPaymentInfoNBDown = false;
  if (lastPaymentMode && lastPaymentInfo?.paymentInfo && lastPaymentMode === toLowerCase(PAYMENT_MODES.NET_BANKING)) {
    lastPaymentInfoNBDown = checkLastPaymentInfoNBDown(paymentInfo);
  }
  // handling of upi_intent as it is checked along with gpay as only intent is present for gpay razorpay
  //for pci if last payment mode cc or dc then hide the accordian
  // if lastPaymentInfo is of nb and the lastPaymentInfo's bank is down , then lastPaymentInfo netbanking will not be visible
  if (lastPaymentMode && lastPaymentInfo?.paymentInfo && lastPaymentMode !== toLowerCase(PAYMENT_MODES.UPI_INTENT) && availableMethods.includes(lastPaymentMode) && !pciEnabled && !lastPaymentInfoNBDown) {
    return true;
  }
  if (lastPaymentMode && lastPaymentInfo?.paymentInfo && lastPaymentMode !== toLowerCase(PAYMENT_MODES.UPI_INTENT) && availableMethods.includes(lastPaymentMode) && pciEnabled && !lastPaymentInfoNBDown) {
    if (lastPaymentMode !== toLowerCase(PAYMENT_MODES.CREDIT_CARD) && lastPaymentMode !== toLowerCase(PAYMENT_MODES.DEBIT_CARD) )
    return true;
  }
  return false;
};
export const isWalletVisible = (walletInfo: WalletInfo, cartInfo: ICartInfo, flow: string): boolean => {
  /**
   * wallet will be visible only on checkout flow;
   */
  if (flow !== PAYMENT_FLOWS.CHECKOUT) return false;
  const scWalletFlag = get(walletInfo, "scWalletFlag");
  const isWalletEnabled = checkIsNykaaWalletEnabled({
    scWalletFlag,
  });
  return isWalletEnabled;
};

export const getRemotePaymentMethods = (isMobile = false) => {
  // default methods
  let remoteMethods = [
    { preferred: ["last", "cc", "upi", "paytm", "simpl"] },
    "cc",
    "upi_intent",
    "upi",
    "nb",
    {
      snpl: ["zest_money", "simpl"],
    },
    "cod",
    "paytm",
    "gift",
    "wallet",
    "emi",
  ];
  let configMethods;
  if (!isServer()) {
    const remotePaymentMethods = get(
      window,
      "remoteConfig.configs.payment.remotePaymentMethods"
    );
    if (!remotePaymentMethods) return remoteMethods;
    if (isMobile) {
      configMethods = get(remotePaymentMethods, "mobile");
    } else {
      configMethods = get(remotePaymentMethods, "desktop");
    }
  }
  if (!checkIsEmptyArray(configMethods)) remoteMethods = configMethods;
  return remoteMethods;
};


/**
 * @description
 * function that handles user redirection to payment preconfirmation page
 * if new payment preconfirmation redirection is true, redirected to new stack payment preconfirmation page
 * redirection to old payment preconfirmation url in other cases
 * @param {*} queryParams - orderId parameter to be appended
 */
export const redirectToPaymentPreConfirmationPage = (
  history: any,
  queryParams: string
) => {
  try {
    if (!isServer()) {
      /**
       * removed payment preconfirmation experiment
       */
      let url = CLIENT_URLS.PAYMENT_PRE_CONFIRMATION_PAGE;
      if (queryParams) {
        url = `${CLIENT_URLS.PAYMENT_PRE_CONFIRMATION_PAGE}?${queryParams}`;
      }

      doRedirect({
        url,
        isClientRedirection: true,
        history,
      });
    }
  } catch (error) {
    // logger.error(error, 'Error in redirectToPaymentPreConfirmationPage function');
  }
};

/**
 * @description
 * function that gets pci experiment cookie value and converts to boolean value
 * @returns experiment value as boolean
 */
 export const getPciExperiment = () => {
  const remoteConfig = window?.remoteConfig;
  const pciCheckoutExperiment = get(remoteConfig, "configs.AB.pciCheckoutExperiment");
  const cookie = get(pciCheckoutExperiment, "cookie");
  let experiment = false;
  // logics for experiment calculation
  try {
    experiment = getCookie(cookie) === "1";
  } catch (error: any) {
    // logger.error(error, 'Error in getCheckoutExperiment function');
  }
  return experiment;
};

/**
 * function for returning shipping Amount
 * shippingChargeStriked value stored in remoteconfig.configs.payment
 * @returns shippingCharge {number}
 */
export const getShippingChargeStriked = () => {
  let shippingCharge = 70;
  if (!isServer()) {
    const remoteConfig = get(window, "remoteConfig");
    const charge = get(remoteConfig, "configs.payment.shippingChargeStriked");
    if (charge > 0) {
      shippingCharge = charge;
    }
  }
  return shippingCharge;
};

/**
 * funtion for returning shipping details
 * this is platform specific.
 */
export const getShippingDetails = (
  cartConvenienceBreakupConfig: ICartConvenienceChargesBreakupDto[],
  shippingText: string,
  isConvenienceChargesExperimentEnabled: boolean
) => cartConvenienceBreakupConfig.length && isConvenienceChargesExperimentEnabled ? cartConvenienceBreakupConfig : shippingText;
